// ─── SUPABASE CLIENT ─────────────────────────────────────────────────────────
// Single source of truth for all database reads and writes.

const SUPABASE_URL = 'https://qhoewnrhypzuzozjrcgq.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_SfpY6GzQvs2F5d5-7ZyKYg_KPunDhSw';

// ─── AUTH ─────────────────────────────────────────────────────────────────────

export async function signIn(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error_description || 'Login failed');
  }
  const data = await res.json();
  localStorage.setItem('trejhara_session', JSON.stringify({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + data.expires_in * 1000,
  }));
  return data;
}

export async function signOut() {
  localStorage.removeItem('trejhara_session');
}

export function getSession() {
  try {
    const raw = localStorage.getItem('trejhara_session');
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (Date.now() > session.expires_at) {
      localStorage.removeItem('trejhara_session');
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export async function refreshSession() {
  try {
    const session = getSession();
    if (!session?.refresh_token) return null;
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const newSession = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: Date.now() + data.expires_in * 1000,
    };
    localStorage.setItem('trejhara_session', JSON.stringify(newSession));
    return newSession;
  } catch {
    return null;
  }
}

// Get valid headers — refresh token if within 5 minutes of expiry
export async function getValidHeaders() {
  let session = getSession();
  if (!session) return null;
  // Refresh if expiring within 5 minutes
  if (session.expires_at - Date.now() < 5 * 60 * 1000) {
    session = await refreshSession();
    if (!session) return null;
  }
  return {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${session.access_token}`,
  };
}

export function isAuthenticated() {
  return !!getSession();
}

function getHeaders() {
  const session = getSession();
  const token = session ? session.access_token : SUPABASE_ANON_KEY;
  return {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${token}`,
  };
}



// ─── GENERIC HELPERS ─────────────────────────────────────────────────────────

async function sbSelect(table, filters = {}) {
  let url = `${SUPABASE_URL}/rest/v1/${table}?select=*`;
  for (const [key, val] of Object.entries(filters)) {
    url += `&${key}=eq.${encodeURIComponent(val)}`;
  }
  // Try with current session, refresh if expired
  let hdrs = getHeaders();
  const res = await fetch(url, { headers: hdrs });
  if (res.status === 401) {
    // Token expired — try refresh
    const refreshed = await refreshSession();
    if (!refreshed) throw new Error('Session expired — please log out and log back in');
    hdrs = getHeaders();
    const res2 = await fetch(url, { headers: hdrs });
    if (!res2.ok) throw new Error(`Supabase select failed after refresh: ${res2.status} ${await res2.text()}`);
    return res2.json();
  }
  if (!res.ok) throw new Error(`Supabase select failed: ${res.status} ${await res.text()}`);
  return res.json();
}

async function sbUpsert(table, record) {
  const url = `${SUPABASE_URL}/rest/v1/${table}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getHeaders(),
      'Prefer': 'resolution=merge-duplicates,return=minimal',
    },
    body: JSON.stringify(record),
  });
  if (!res.ok) throw new Error(`Supabase upsert failed: ${res.status} ${await res.text()}`);
}

// ─── OPS DATA (UAE + Saudi operational) ──────────────────────────────────────

export async function loadOpsData() {
  try {
    const rows = await sbSelect('trejhara_ops');
    const result = { uae: emptyEntityData(), saudi: emptyEntityData() };
    for (const row of rows) {
      if (row.entity === 'uae' || row.entity === 'saudi') {
        result[row.entity] = row.data;
      }
    }
    return result;
  } catch (e) {
    console.error('Failed to load ops data from Supabase:', e);
    return { uae: emptyEntityData(), saudi: emptyEntityData() };
  }
}

export async function saveOpsData(entity, entityData) {
  try {
    // Check if row exists
    const existing = await sbSelect('trejhara_ops', { entity });
    if (existing.length > 0) {
      // Update
      const url = `${SUPABASE_URL}/rest/v1/trejhara_ops?entity=eq.${entity}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ data: entityData, updated_at: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    } else {
      // Insert
      await sbUpsert('trejhara_ops', {
        entity,
        data: entityData,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error(`Failed to save ops data for ${entity}:`, e);
    throw e;
  }
}

// ─── MONTHLY MIS ─────────────────────────────────────────────────────────────

export async function loadMISData(entity) {
  try {
    const rows = await sbSelect('trejhara_mis', { entity });
    return rows.length > 0 ? rows[0].data : null;
  } catch (e) {
    console.error(`Failed to load MIS data for ${entity}:`, e);
    return null;
  }
}

export async function saveMISData(entity, pack) {
  const hdrs = await getValidHeaders();
  if (!hdrs) throw new Error('Session expired — please log out and log back in');
  try {
    const existing = await sbSelect('trejhara_mis', { entity });
    if (existing.length > 0) {
      const url = `${SUPABASE_URL}/rest/v1/trejhara_mis?entity=eq.${entity}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: hdrs,
        body: JSON.stringify({ data: pack, updated_at: new Date().toISOString() }),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`MIS save failed (${res.status}): ${errText}`);
      }
    } else {
      await sbUpsert('trejhara_mis', {
        entity,
        data: pack,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error(`Failed to save MIS data for ${entity}:`, e);
    throw e;
  }
}

// ─── CLIENT MASTER ────────────────────────────────────────────────────────────

export async function loadClientMasterDB() {
  try {
    const rows = await sbSelect('trejhara_client_master');
    return rows.length > 0 ? rows[0].data : {};
  } catch (e) {
    console.error('Failed to load client master:', e);
    return {};
  }
}

export async function saveClientMasterDB(master) {
  try {
    const existing = await sbSelect('trejhara_client_master');
    if (existing.length > 0) {
      const url = `${SUPABASE_URL}/rest/v1/trejhara_client_master?id=eq.${existing[0].id}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ data: master, updated_at: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    } else {
      await sbUpsert('trejhara_client_master', {
        data: master,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error('Failed to save client master:', e);
    throw e;
  }
}

// ─── UTILS ───────────────────────────────────────────────────────────────────

export function emptyEntityData() {
  return { jobs: [], ar: [], cashflow: null, loaded: { jobs: false, ar: false, cashflow: false } };
}

// ─── JOB HISTORY (3-month rolling) ───────────────────────────────────────────

export async function loadJobsHistory(entity) {
  try {
    const rows = await sbSelect('trejhara_jobs_history', { entity });
    return rows.length > 0 ? rows[0].data : [];
  } catch (e) {
    console.error(`Failed to load jobs history for ${entity}:`, e);
    return [];
  }
}

export async function saveJobsHistory(entity, jobs) {
  try {
    const existing = await sbSelect('trejhara_jobs_history', { entity });
    if (existing.length > 0) {
      const url = `${SUPABASE_URL}/rest/v1/trejhara_jobs_history?entity=eq.${entity}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ data: jobs, updated_at: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    } else {
      await sbUpsert('trejhara_jobs_history', {
        entity,
        data: jobs,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error(`Failed to save jobs history for ${entity}:`, e);
    throw e;
  }
}

// ─── ALERT DISMISSALS ─────────────────────────────────────────────────────────

export async function loadAlertDismissals(entity) {
  try {
    const rows = await sbSelect('trejhara_alert_dismissals', { entity });
    const result = {};
    for (const row of rows) {
      result[row.alert_type] = row.excluded_job_types || [];
    }
    return result;
  } catch (e) {
    console.error('Failed to load alert dismissals:', e);
    return {};
  }
}

export async function saveAlertDismissal(entity, alertType, excludedJobTypes) {
  try {
    const existing = await sbSelect('trejhara_alert_dismissals', { entity, alert_type: alertType });
    if (existing.length > 0) {
      const url = `${SUPABASE_URL}/rest/v1/trejhara_alert_dismissals?entity=eq.${entity}&alert_type=eq.${encodeURIComponent(alertType)}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ excluded_job_types: excludedJobTypes, updated_at: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    } else {
      await sbUpsert('trejhara_alert_dismissals', {
        entity,
        alert_type: alertType,
        excluded_job_types: excludedJobTypes,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error('Failed to save alert dismissal:', e);
    throw e;
  }
}
